import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import type { OnboardingDraft } from './types';

type ProjectItem = NonNullable<OnboardingDraft['projects']>[number];

type ProjectsEditModalProps = {
  show: boolean;
  items: ProjectItem[];
  saving?: boolean;
  onHide: () => void;
  onSave: (items: ProjectItem[]) => Promise<boolean>;
};

export default function ProjectsEditModal({
  show,
  items,
  saving = false,
  onHide,
  onSave,
}: ProjectsEditModalProps) {
  const [draftItems, setDraftItems] = useState<ProjectItem[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setDraftItems(
        items.length
          ? items.map((item) => ({ ...item }))
          : [{ name: '', description: '', link: '' }]
      );
    }
  }, [show, items]);

  const updateItem = (index: number, patch: Partial<ProjectItem>) => {
    setDraftItems((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
    );
  };

  const addItem = () => {
    setDraftItems((prev) => [...prev, { name: '', description: '', link: '' }]);
  };

  const removeItem = (index: number) => {
    setDraftItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSave = async () => {
    const cleaned = draftItems.filter(
      (item) => (item.name || '').trim() || (item.link || '').trim()
    );
    setSubmitting(true);
    try {
      const saved = await onSave(cleaned);
      if (saved) {
        onHide();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const busy = saving || submitting;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="ss-app-modal">
      <Modal.Header closeButton>
        <Modal.Title>Edit projects</Modal.Title>
      </Modal.Header>
      <Modal.Body className="ssr-modal-body">
        {draftItems.map((item, index) => (
          <div className="ssr-exp-edit-block" key={`project-edit-${index}`}>
            <div className="ssr-card-head">
              <p className="ssr-exp-edit-label">Project {index + 1}</p>
              {draftItems.length > 1 ? (
                <button type="button" className="ssr-edit-link" onClick={() => removeItem(index)}>
                  Remove
                </button>
              ) : null}
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                className="ssr-field-input"
                value={item.name || ''}
                onChange={(event) => updateItem(index, { name: event.target.value })}
                placeholder="Project name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Link (optional)</Form.Label>
              <Form.Control
                className="ssr-field-input"
                value={item.link || ''}
                onChange={(event) => updateItem(index, { link: event.target.value })}
                placeholder="https://…"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                className="ssr-field-input ssr-field-textarea ssr-field-textarea--lg"
                value={item.description || ''}
                onChange={(event) => updateItem(index, { description: event.target.value })}
                placeholder="What you built and the impact"
              />
            </Form.Group>
          </div>
        ))}
        <Button variant="outline-success" type="button" onClick={addItem}>
          Add another project
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant="success" onClick={() => void handleSave()} disabled={busy}>
          {busy ? 'Saving…' : 'Save projects'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
